import { createI18n } from 'vue-i18n'
import zh from './zh-CN'
import zhHk from './zh-HK'
import en from './en-AU'
import { appendLang, readLangParam, stripLangParam } from './langParam'
import { queryClient } from '@/api/queryClient'

/**
 * 语言代码与后端一致（zh-CN / zh-HK / en-AU）—— 请求头 Accept-Language 直接用它，
 * 后端据此渲染展示文案，前端不做映射（沿用）。
 *
 * zh-HK 香港繁体 2026-10-12 加：后端原生出繁体（词条 / 邮件模板），**前端仍不翻译后端返回的文案**；
 * 繁体缺词条时后端回落简体、前端 i18n 回落英文（fallbackLocale），两条链各管各的。
 *
 * 需求 §18：英文是一期硬要求；页脚英文原文在中文模式下不翻译；
 * 动态内容（金额、账号、汇率）不参与翻译。
 */
export const LOCALES = ['zh-CN', 'zh-HK', 'en-AU'] as const
export type Locale = (typeof LOCALES)[number]

const KEY = 'zr.portal.locale'

/**
 * 进站地址上的 `?lang=`（官网 / 用户中心 / 开户站互跳时带）：优先于 localStorage，读到就记下来；
 * 不论合法与否都从地址栏去掉——只在进站那一刻生效，留着的话刷新会把客户后来在站内的切换覆盖回去。
 * ⚠️ 必须在 router 创建之前执行：createWebHistory 一创建就读当前地址，晚了参数会被带进首个路由。
 * 本模块在 i18n 初始化时调用它，main.ts 因此把 `./locales` 放在第一个 import。
 */
function fromUrl(): Locale | null {
  try {
    const lang = readLangParam(window.location.search)
    const stripped = stripLangParam(window.location.href)
    if (stripped !== null) window.history.replaceState(window.history.state, '', stripped)
    if (lang) localStorage.setItem(KEY, lang)
    return lang
  } catch {
    return null
  }
}

function detect(): Locale {
  const fromParam = fromUrl()
  if (fromParam) return fromParam
  try {
    const saved = localStorage.getItem(KEY)
    if (saved && (LOCALES as readonly string[]).includes(saved)) return saved as Locale
  } catch {
    /* ignore */
  }
  /**
   * **默认英文**（用户 2026-09-14 拍板）。这是面向澳洲零售客户的持牌产品：
   * 默认语言是产品定位的一部分，不该由客户的浏览器设置决定。
   *
   * 原来是「跟随浏览器：中文浏览器 → 中文，否则英文」，于是同一个客户在公司电脑与家里电脑
   * 看到的是两种语言，客服与走查也没法说「客户看到的是哪一句」。
   * 客户**显式**切换过的选择（上面那条 localStorage）仍然优先 —— 默认只在没选过时生效。
   */
  return 'en-AU'
}

export const i18n = createI18n({
  legacy: false,
  locale: detect(),
  fallbackLocale: 'en-AU',
  messages: { 'zh-CN': zh, 'zh-HK': zhHk, 'en-AU': en },
  // 缺键时不在控制台刷警告，但保留 fallback 行为
  missingWarn: false,
  fallbackWarn: false,
})

export function setLocale(l: Locale) {
  const changed = i18n.global.locale.value !== l
  i18n.global.locale.value = l
  try {
    localStorage.setItem(KEY, l)
  } catch {
    /* ignore */
  }
  document.documentElement.lang = l
  /*
   * 接口返回的展示文案（合规提醒、汇率披露句、出入金状态、宣传位标题…）是后端按请求头 Accept-Language 渲染的，
   * 缓存键不带语言——换了语言不重拉，页面上就还是切换前那种语言（2026-10-21 dev：英文界面首页证件到期横幅仍是中文）。
   * 正在显示的查询立即重拉，其余标记过期、下次进页面再拉；门户的 useQuery 全是 GET，重拉没有副作用
   */
  if (changed) void queryClient.invalidateQueries()
}

export function currentLocale(): Locale {
  return i18n.global.locale.value as Locale
}

/** 跨站出站链接带上当前语言。在渲染 / 点击时调用：取的是那一刻的语言，客户中途切换过也跟得上 */
export function withLang(url: string): string {
  return appendLang(url, currentLocale())
}

/** 在 setup 之外（路由守卫、http 拦截器）取文案 */
export const t = i18n.global.t
